/**
 * ImportDialog — the four-step bulk-import flow (ORG_IMPORT_DESIGN_2026-08-06 §11.3).
 *
 * ONE DIALOG, NOT A WIZARD ACROSS ROUTES
 * --------------------------------------
 * The admin must be able to abandon this without losing their place on the page,
 * and a route change would lose the parsed file. So: one `<Dialog>`, a `Stepper`
 * at the top, four steps.
 *
 * THE APPLY BUTTON IS ABSENT, NOT DISABLED
 * ----------------------------------------
 * All-or-nothing means there is no partial action to offer, and a greyed button
 * invites hunting for the way to enable it (§3.3). The API agrees rather than
 * merely permitting it: a report with findings carries no `token`, so there is
 * nothing to apply with even from a hand-rolled request.
 *
 * TWO CONFIRMATIONS
 * -----------------
 * One to run the check, which says in its own copy that nothing is written; one
 * to apply, which names the overwrite explicitly when there is one. Cheap to say,
 * and it is what makes an admin willing to press the button.
 *
 * THE ANNOTATED CSV IS BUILT HERE, NOT SERVER-SIDE
 * ------------------------------------------------
 * §3.2's downloadable report is the uploaded file plus `_error_column` and
 * `_error_message`. The message text only exists once a locale is known, and the
 * backend has no locale — it returns `{code, args}`. So the file is assembled in
 * the browser, where `t()` is.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Step,
  StepLabel,
  Stack,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

import {
  importApi,
  type ImportFinding,
  type ImportReport,
} from '../../api/organization';
import type { ImportEntity } from '../../api/organization';
import { Link as RouterLink } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { FIELD_TEXT_INSET } from '../../theme/shellMetrics';
import { ConfirmDialog } from '../primitives';
import { translateApiError } from '../../i18n/translateApiError';

/** How many findings the report lists inline before "showing the first N". */
const INLINE_FINDINGS = 10;
/** How many created codes the summary names before "+N more". */
const INLINE_LABELS = 6;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful apply or undo, so the page can refetch. */
  onApplied: () => void;
  /**
   * Which sheet this dialog drives. Defaults to `departments` so the original
   * call site keeps working unchanged.
   *
   * ONE prop rather than a second dialog: every guarantee the four steps carry —
   * the token is over the PLAN, Apply is ABSENT rather than disabled when the
   * report has findings, the file is re-sent instead of cached — would have to be
   * re-established in a copy, and one copy would drift.
   */
  entity?: ImportEntity;
}

type Step0to3 = 0 | 1 | 2 | 3;

export const ImportDialog: React.FC<Props> = ({
  open,
  onClose,
  onApplied,
  entity = 'departments',
}) => {
  const { t } = useTranslation();
  // Memoised so the object identity is stable across renders: the callbacks below
  // list it as a dependency, and a fresh client per render would make every one of
  // them a new function (session-discipline §11).
  const api = useMemo(() => importApi(entity), [entity]);
  // Seven of this dialog's strings NAME the entity, and they cannot be built by
  // interpolating a noun: Arabic's `ستُحذف الأقسام` carries a definite article the
  // verb agrees with, and German's `die geänderten …` is elliptical, so the
  // adjective ending depends on the noun that was dropped. So the sentences are
  // written per entity, per locale (i18n-nine-locales §8c), and this is the single
  // place that picks the block.
  const ek = (key: string) => `org:import.entities.${entity}.${key}`;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<Step0to3>(0);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [result, setResult] = useState<{
    batchId: string;
    created: number;
    updated: number;
    unchanged: number;
    invitationsSent: number;
    outstandingDocuments: {
      workers: number;
      documents: number;
      types: string[];
    } | null;
  } | null>(null);
  const [undone, setUndone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);
  // §8.1 — the admin's explicit press on "create these N and continue". OFF by
  // default, and part of the token, so a plain Apply cannot create labels they
  // never agreed to.
  const [createMissing, setCreateMissing] = useState(false);
  // §3.4 — invitations OFF by default. A bulk import that emails 200 people the
  // moment it lands is not a default anybody would choose on purpose.
  const [sendInvitations, setSendInvitations] = useState(false);

  const reset = useCallback(() => {
    setStep(0);
    setFile(null);
    setReport(null);
    setResult(null);
    setUndone(false);
    setBusy(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return;
    reset();
    onClose();
  }, [busy, onClose, reset]);

  /** Localize one finding, preferring the `_suggestion` variant when there is one. */
  const messageFor = useCallback(
    (finding: ImportFinding): string => {
      const args = (finding.args ?? {}) as Record<string, unknown>;
      const key =
        args.suggestion !== undefined
          ? `org:import.errors.${finding.code}_suggestion`
          : `org:import.errors.${finding.code}`;
      return t(key, {
        ...args,
        column: finding.column ?? '',
        value: finding.value ?? '',
      });
    },
    [t],
  );

  const download = async (example: boolean) => {
    setError(null);
    try {
      await api.downloadTemplate(example);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('common:feedback.errorGeneric'),
      );
    }
  };

  const pick = (chosen: File | null) => {
    setFile(chosen);
    setReport(null);
    setError(null);
  };

  const runCheck = async (withCreate = createMissing) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      // Re-running the CHECK rather than applying: the admin must see the report
      // that the creation produces before anything is written, which is the whole
      // of §2's promise.
      const next = await api.validate(file, withCreate);
      setCreateMissing(withCreate);
      setReport(next);
      setStep(2);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('common:feedback.errorGeneric'),
      );
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!file || !report?.token) return;
    setBusy(true);
    setError(null);
    try {
      const committed = await api.commit(file, report.token, {
        createMissing,
        sendInvitations,
      });
      setResult({
        batchId: committed.batch_id,
        created: committed.created_count,
        updated: committed.updated_count,
        unchanged: committed.unchanged_count,
        invitationsSent: committed.invitations_sent ?? 0,
        outstandingDocuments: committed.outstanding_documents
          ? {
              workers: committed.outstanding_documents.workers ?? 0,
              documents: committed.outstanding_documents.documents ?? 0,
              types: committed.outstanding_documents.types ?? [],
            }
          : null,
      });
      setStep(3);
      onApplied();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('common:feedback.errorGeneric'),
      );
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!result) return;
    setBusy(true);
    setError(null);
    try {
      setConfirmUndo(false);
      await api.undo(result.batchId);
      setUndone(true);
      onApplied();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('common:feedback.errorGeneric'),
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * §3.2 — the uploaded file with two columns appended, so the customer fixes
   * the flagged rows where the data is instead of transcribing line numbers
   * between a screen and a spreadsheet.
   */
  const downloadAnnotated = async () => {
    if (!file || !report) return;
    const text = await file.text();
    const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
    const byLine = new Map<number, ImportFinding[]>();
    const fileWide: ImportFinding[] = [];
    findings.forEach((finding) => {
      if (finding.line == null) fileWide.push(finding);
      else byLine.set(finding.line, [...(byLine.get(finding.line) ?? []), finding]);
    });

    const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const annotated = lines.map((line, index) => {
      const lineNumber = index + 1;
      const findings = byLine.get(lineNumber);
      if (!line) return line;
      if (line.startsWith('#')) return line;
      if (!findings) return line;
      return [
        line,
        quote(findings.map((f) => f.column ?? '').join(' ')),
        quote(findings.map(messageFor).join(' ')),
      ].join(',');
    });

    // Column-level and file-level findings have no row to sit on, so they go
    // above the data as comment lines — which the reader skips on re-upload.
    const preamble = fileWide.map((f) => `# ${messageFor(f)}`);
    const blob = new Blob(
      ['\uFEFF', [...preamble, ...annotated].join('\r\n')],
      { type: 'text/csv;charset=utf-8' },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name.replace(/\.csv$/i, '') + '-problems.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  /**
   * The generated schema marks every field with a server-side default as
   * OPTIONAL, so `report.findings` is `T[] | undefined` even though the backend
   * always sends an array. Normalised once here rather than with `?? []` at each
   * of the eight read sites, where one omission is a runtime crash.
   */
  const findings = useMemo(() => report?.findings ?? [], [report]);
  const createdLabels = useMemo(() => report?.created_labels ?? [], [report]);
  const changedColumns = useMemo(() => report?.changed_columns ?? {}, [report]);
  const dateReadings = useMemo(() => report?.date_readings ?? [], [report]);
  const unknownValues = useMemo(() => report?.unknown_values ?? [], [report]);

  const hasProblems = findings.length > 0;
  const canApply = Boolean(report && !hasProblems && report.token);

  const inlineFindings = useMemo(
    () => findings.slice(0, INLINE_FINDINGS),
    [findings],
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.0625rem' }}>
          {t(ek('title'))}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {(['get', 'upload', 'report', 'done'] as const).map((key) => (
              <Step key={key}>
                <StepLabel>{t(`org:import.steps.${key}`)}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {step === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('org:import.step1.lead')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => download(false)}
                >
                  {t('org:import.step1.downloadCurrent')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => download(true)}
                >
                  {t('org:import.step1.downloadTemplate')}
                </Button>
              </Box>
              <Typography variant="caption" color="text.disabled">
                {t('org:import.step1.hint')}
              </Typography>
            </Box>
          )}

          {step === 1 && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pick(e.dataTransfer.files?.[0] ?? null);
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('org:import.step2.drop')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {file ? t('org:import.step2.replace') : t('org:import.step2.choose')}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  aria-label={t('org:import.step2.choose')}
                  onChange={(e) => pick(e.target.files?.[0] ?? null)}
                />
                {file && (
                  <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
                    {t('org:import.step2.selected', {
                      name: file.name,
                      size: Math.max(1, Math.round(file.size / 1024)),
                    })}
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {step === 2 && report && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('org:import.report.rowsRead', { count: report.rows_read })}
              </Typography>

              {hasProblems ? (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('org:import.report.problems', {
                        count: findings.length,
                      })}
                    </Typography>
                    <Typography variant="body2">
                      {t('org:import.report.problemsLead')}
                    </Typography>
                  </Alert>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('org:import.report.colLine')}</TableCell>
                        <TableCell>{t('org:import.report.colColumn')}</TableCell>
                        <TableCell>{t('org:import.report.colValue')}</TableCell>
                        <TableCell>{t('org:import.report.colProblem')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inlineFindings.map((finding, index) => (
                        <TableRow key={`${finding.code}-${finding.line}-${index}`}>
                          <TableCell>
                            {finding.line ?? t('org:import.report.wholeFile')}
                          </TableCell>
                          <TableCell>{finding.column ?? '—'}</TableCell>
                          <TableCell>{finding.value ?? '—'}</TableCell>
                          <TableCell>{messageFor(finding)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {findings.length > INLINE_FINDINGS && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {t('org:import.report.showingFirst', { count: INLINE_FINDINGS })}
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={downloadAnnotated}
                      sx={{ mt: 2 }}
                  >
                    {t('org:import.report.downloadAnnotated')}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>{report.created_count}</strong>{' '}
                    {t('org:import.report.new')} ·{' '}
                    <strong>{report.updated_count}</strong>{' '}
                    {t('org:import.report.updated')} ·{' '}
                    <strong>{report.unchanged_count}</strong>{' '}
                    {t('org:import.report.unchanged')}
                  </Typography>

                  {createdLabels.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                      {createdLabels.slice(0, INLINE_LABELS).map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                        />
                      ))}
                      {report.created_count > INLINE_LABELS && (
                        <Typography variant="caption" color="text.disabled">
                          {t('org:import.report.moreLabels', {
                            count: report.created_count - INLINE_LABELS,
                          })}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/*
                    The load-bearing part of the confirm screen: WHICH columns
                    change, not just how many rows. "27 updated" cannot warn an
                    admin that a stale export is about to rename 27 departments
                    back (§4.1).
                  */}
                  {Object.keys(changedColumns).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('org:import.report.columnsChange')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {Object.entries(changedColumns).map(([column, count]) => (
                          <Chip
                            key={column}
                            label={t('org:import.report.columnCount', { column, count })}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {dateReadings.map((reading) => (
                    <Typography
                      key={reading.column}
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {t('org:import.report.dateRead', {
                        column: reading.column,
                        reading: t(
                          `org:import.report.reading${
                            reading.reading.charAt(0).toUpperCase() +
                            reading.reading.slice(1)
                          }`,
                        ),
                        value: reading.proof_value ?? '',
                        line: reading.proof_line ?? '',
                      })}
                    </Typography>
                  ))}

                  {report.created_count === 0 && report.updated_count === 0 && (
                    <Typography variant="body2" color="text.disabled">
                      {t('org:import.report.nothingToDo')}
                    </Typography>
                  )}

                  {/* §3.4 — OFF by default. A bulk import that emails 200 people
                      the moment it lands is not a default anybody would choose on
                      purpose, and the result step reports how many actually went
                      out rather than implying all of them did. */}
                  {entity === 'workers' && report.created_count > 0 && (
                    <FormControlLabel
                      sx={{ mt: 1.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={sendInvitations}
                          onChange={(e) => setSendInvitations(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t('org:import.invitations.label', {
                            count: report.created_count,
                          })}
                        </Typography>
                      }
                    />
                  )}
                </>
              )}

                {/* §8.1 — the unknown FK values, BY NAME with their lines and a
                    near match where one exists. Never a count: "3 new departments
                    will be created" reads fine while the third is `Engineerng`, a
                    typo on row 40 that is about to become a real department with
                    real workers in it. The near-match line is what turns that
                    typo from an invisible creation into the obvious thing on
                    screen. */}
                {unknownValues.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      py: 1.5,
                      px: `${FIELD_TEXT_INSET}px`,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {t('org:import.unknowns.title', {
                        count: unknownValues.length,
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {report.can_create_unknowns
                        ? t('org:import.unknowns.body')
                        : t('org:import.unknowns.tooMany', {
                            count: unknownValues.length,
                          })}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                      {unknownValues.map((unknown) => (
                        <Typography
                          key={`${unknown.column}:${unknown.value}`}
                          variant="body2"
                        >
                          <strong>{unknown.value}</strong>
                          {' · '}
                          {t(`org:import.unknowns.columns.${unknown.column}`, {
                            defaultValue: unknown.column,
                          })}
                          {unknown.suggestion ? (
                            <Box component="span" sx={{ color: 'warning.main' }}>
                              {' · '}
                              {t('org:import.unknowns.didYouMean', {
                                suggestion: unknown.suggestion,
                              })}
                            </Box>
                          ) : null}
                          <Box component="span" sx={{ color: 'text.disabled' }}>
                            {' · '}
                            {t('org:import.unknowns.lines', {
                              lines: (unknown.lines ?? []).join(', '),
                            })}
                          </Box>
                        </Typography>
                      ))}
                    </Stack>
                    {report.can_create_unknowns && !createMissing && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => runCheck(true)}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('org:import.unknowns.createAndContinue', {
                          count: unknownValues.length,
                        })}
                      </Button>
                    )}
                    {createMissing && (
                      <Typography variant="caption" color="success.main">
                        {t('org:import.unknowns.willBeCreated', {
                          count: unknownValues.length,
                        })}
                      </Typography>
                    )}
                  </Box>
                )}
            </Box>
          )}

          {step === 3 && result && (
            <Box>
              <Alert severity={undone ? 'info' : 'success'} sx={{ mb: 2 }}>
                {undone
                  ? t(ek('undone'))
                  : t('org:import.done.summary', {
                      created: result.created,
                      updated: result.updated,
                      unchanged: result.unchanged,
                    })}
              </Alert>
              {/* §3.4 — reported, not implied. Delivery is best-effort and happens
                  outside the transaction, so "40 workers added" must never be read
                  as "40 people can sign in". */}
              {!undone && sendInvitations && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {result.invitationsSent > 0
                    ? t('org:import.invitations.sent', {
                        count: result.invitationsSent,
                      })
                    : t('org:import.invitations.noneSent')}
                </Typography>
              )}
              {/* ADR-044 §3 / S6 — the debt, and a route to where the work is
                  done. The import deliberately does NOT block: a spreadsheet row
                  cannot carry a passport scan, and five of the six other
                  worker-creation paths cannot either, so blocking would take
                  automated provisioning away from exactly the tenants who care most
                  about compliance. What it must not do is stay silent — an admin who
                  ticked "required" and imported 40 people believes they have 40
                  compliant records.

                  Plain tone, not danger: nothing is wrong, there is work left. */}
              {!undone && result.outstandingDocuments && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {t('org:import.documents.outstanding', {
                      workers: result.outstandingDocuments.workers,
                      documents: result.outstandingDocuments.documents,
                      types: result.outstandingDocuments.types.join(', '),
                    })}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    component={RouterLink}
                    to="/organization/workers/documents"
                    onClick={onClose}
                    sx={{ textTransform: 'none', px: 0, mt: 0.5 }}
                  >
                    {t('org:import.documents.goToDocuments')}
                  </Button>
                </Alert>
              )}
              {!undone && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setConfirmUndo(true)}
                  disabled={busy}
                >
                  {t('org:import.done.undo')}
                </Button>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            {step === 3 ? t('org:import.done.close') : t('common:actions.cancel')}
          </Button>

          {step === 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => setStep(1)}
            >
              {t('org:import.step1.next')}
            </Button>
          )}

          {step === 1 && (
            <Button
              variant="contained"
              size="small"
              disabled={!file || busy}
              onClick={() => setConfirmCheck(true)}
              startIcon={busy ? <CircularProgress size={14} /> : undefined}
            >
              {t('org:import.step2.check')}
            </Button>
          )}

          {/*
            ABSENT, not disabled. There is no partial apply to offer, and the
            report carries no token to apply with.
          */}
          {step === 2 && canApply && (
            <Button
              variant="contained"
              size="small"
              disabled={busy}
              onClick={() => setConfirmApply(true)}
            >
              {t('org:import.report.apply')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmCheck}
        title={t('org:import.confirm1.title')}
        description={t('org:import.confirm1.body', { name: file?.name ?? '' })}
        confirmLabel={t('org:import.confirm1.action')}
        onConfirm={runCheck}
        onClose={() => setConfirmCheck(false)}
      />

      <ConfirmDialog
        open={confirmApply}
        title={t('org:import.confirm2.title')}
        // Danger tone only when something is being overwritten; plain when it is
        // creates only (§11.3).
        tone={(report?.updated_count ?? 0) > 0 ? 'warning' : 'primary'}
        description={
          (report?.updated_count ?? 0) > 0
            ? t(ek('confirmWithUpdates'), { count: report?.updated_count ?? 0 })
            : t(ek('confirmCreateOnly'), { count: report?.created_count ?? 0 })
        }
        confirmLabel={t('org:import.confirm2.action')}
        onConfirm={apply}
        onClose={() => setConfirmApply(false)}
      />

      {/* Undo is DESTRUCTIVE — it deletes the rows the import created — and it
          was firing straight from the button with no confirmation, while
          `history.confirmUndoTitle`, `confirmUndoAction` and the per-entity
          `confirmUndoBody` sat translated in nine locales and unrendered. That is
          the svdy-ui §12.12 shape: written, wired into the catalog, never mounted.
          Making the copy true is the fix; deleting it would have removed the
          confirmation this action should always have had. */}
      <ConfirmDialog
        open={confirmUndo}
        title={t('org:import.history.confirmUndoTitle')}
        tone="warning"
        description={t(ek('confirmUndoBody'))}
        confirmLabel={t('org:import.history.confirmUndoAction')}
        onConfirm={undo}
        onClose={() => setConfirmUndo(false)}
      />
    </>
  );
};

export default ImportDialog;
